import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Text from '../components/elements/TextComponent';
import Button from '../components/elements/Button';
import ImageComponent from '../components/elements/ImageComponent';
import userAtDesk from '../assets/userAtDesk.svg';

const EFFECTIVE_DATE = 'July 30, 2025';
const PRODUCT_NAME = 'ComeChatWithMe.ai';
const CONTACT_EMAIL = 'comechatwithmeai@gmail.com';

const MobilePrivacy: React.FC = () => {
  const navigate = useNavigate();
  const [imageSize, setImageSize] = useState(160);
  const whiteSectionRef = useRef<HTMLDivElement>(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);

  useEffect(() => {
    const update = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = window.innerHeight - vh;
      setKeyboardOpen(keyboardHeight > 100);
      const w = window.innerWidth;
      const size = Math.min(w * 0.45, vh * 0.3);
      setImageSize(Math.max(140, Math.min(size, 220)));
    };
    update();
    window.visualViewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      window.visualViewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      style={{
        height: '100vh',
        fontFamily: 'Montserrat',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 240,
          backgroundColor: 'var(--primary)',
          padding: '24px 16px',
          display: keyboardOpen ? 'none' : 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <ImageComponent
          src={userAtDesk}
          center
          style={{ width: `${imageSize}px`, height: `${imageSize}px`, objectFit: 'contain' }}
        />
        <div>
          <Text
            text={PRODUCT_NAME}
            fontSize="24px"
            fontWeight="700"
            color="#ffffff"
            textAlign="center"
            navigateToHome
          />
          <Text
            text="Your privacy matters."
            fontSize="14px"
            fontWeight="400"
            color="#ffffff"
            textAlign="center"
          />
        </div>
      </div>

      <div
        ref={whiteSectionRef}
        style={{
          flex: 1,
          backgroundColor: '#ffffff',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          overflowY: 'auto',
          padding: '24px 20px 160px',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <Text text="Privacy Policy" fontSize="24px" fontWeight="700" color="#030303" textAlign="left" />
        <div style={{ marginTop: 12 }}>
          <Text text={`Effective Date: ${EFFECTIVE_DATE}`} fontSize="14px" fontWeight="400" color="#4a4a4a" textAlign="left" />
          <Text text={`Owner: Isaac Joseph Pinchas Hooge  •  Contact: ${CONTACT_EMAIL}`} fontSize="14px" fontWeight="400" color="#4a4a4a" textAlign="left" />
        </div>

        <Section title="1. Introduction">
          {`${PRODUCT_NAME} lets users create and share chatbots. This Privacy Policy explains what we collect, how we use it, and your rights. By using the Platform, you agree to these practices.`}
        </Section>
        <Section title="2. Information We Collect">
                <>
                  <Subheading text="From Users (Bot Owners):" />
                  <Bullet>
                    Email address (for login/account management)
                  </Bullet>
                  <Bullet>
                    Uploaded resumes, profiles, documents, and any information you provide to power your chatbot
                  </Bullet>
                  <Bullet>
                    Subscription status and billing metadata (payments processed by third-party payment processors)
                  </Bullet>
                  <Bullet>
                    Settings and preferences
                  </Bullet>

                  <Subheading text="From Visitors/Recruiters (who chat with bots):" />
                  <Bullet>
                    Chat messages sent to the chatbot and their timestamps
                  </Bullet>
                  <Bullet>
                    Basic device, browser, and IP information (for security and analytics)
                  </Bullet>
                </>
              </Section>
        <Section title="3. How We Use Data">
                <>
                  <Bullet>To provide and maintain the chatbot services</Bullet>
                  <Bullet>To display visitor/recruiter conversations to the bot owner</Bullet>
                  <Bullet>To authenticate accounts and manage subscriptions</Bullet>
                  <Bullet>To improve, troubleshoot, and secure the Platform</Bullet>
                  <Bullet>To send service-related notifications</Bullet>
                </>
              </Section>

        <Section title="4. Legal Bases (GDPR)">
                <>
                  <Bullet>Performance of a contract (providing the service)</Bullet>
                  <Bullet>Legitimate interests (security, service improvement)</Bullet>
                  <Bullet>Consent (where required, e.g., certain cookies)</Bullet>
                  <Bullet>Legal obligation (complying with applicable law)</Bullet>
                </>
              </Section>
        <Section title="5. Sharing & Processors">
          {`We do not sell personal data. We may share data with trusted service providers (e.g., cloud hosting, analytics, payment processors) who process data on our behalf and under contractual safeguards. We may also share data if required by law or to protect rights, property, or safety.`}
        </Section>
        <Section title="6. Retention">
          {`We keep data while your account is active or as needed to provide the service. You may request deletion at any time by emailing ${CONTACT_EMAIL}. We may retain certain information as required by law.`}
        </Section>
        <Section title="7. Your Rights (GDPR & CCPA)">
                <>
                  <Bullet>Access, correct, or delete your data</Bullet>
                  <Bullet>Object to or restrict processing in certain cases</Bullet>
                  <Bullet>Data portability (receive a copy in machine-readable format)</Bullet>
                  <Bullet>California users: the right to know, delete, and opt out of “sale” (we do not sell personal data)</Bullet>
                  <div style={{ marginTop: 8 }}>
                    <Text
                      text={`To exercise rights, email ${CONTACT_EMAIL} with "Privacy Request" in the subject line.`}
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="22px"
                      color="#030303"
                      textAlign="left"
                    />
                  </div>
                </>
              </Section>
        <Section title="8. Cookies & Tracking">
          {`We use cookies and similar technologies for authentication, analytics, and functionality. You can control cookies via your browser settings; disabling them may impact features.`}
        </Section>
        <Section title="9. Security">
          {`We use industry-standard measures (encryption, access controls) to protect personal information. However, no system is 100% secure.`}
        </Section>
        <Section title="10. Children’s Privacy">
          {`The service is not intended for children under 13 (or the age of consent in your jurisdiction). We do not knowingly collect data from children.`}
        </Section>
        <Section title="11. International Transfers">
          {`Your data may be processed in countries other than your own. Where required, we implement appropriate safeguards for cross-border transfers.`}
        </Section>
        <Section title="12. Changes">
          {`We may update this policy from time to time. If changes are material, we will notify you via email or in-app notice.`}
        </Section>
        <Section title="13. Contact">
          {`For questions, contact: ${CONTACT_EMAIL}.`}
        </Section>

        <div style={{ display: 'flex', gap: 12, marginTop: '20px'}}>
          <Button
            backgroundColor="var(--primary)"
            color="#ffffff"
            label="Back to Login"
            onClick={() => navigate('/login')}
          />
          <Button
            backgroundColor="#eaeaea"
            color="#030303"
            label="Terms of Service"
            onClick={() => navigate('/termsofservice')}
          />
        </div>
      </div>
    </div>
  );
};

export default MobilePrivacy;

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginTop: 20 }}>
    <Text text={title} fontSize="18px" fontWeight="700" lineHeight="24px" color="#030303" textAlign="left" />
    <div style={{ marginTop: 10 }}>{children}</div>
  </div>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
    <Text text="•" fontSize="14px" fontWeight="700" lineHeight="22px" color="#030303" textAlign="left" />
    <Text text={String(children)} fontSize="14px" fontWeight="400" lineHeight="22px" color="#030303" textAlign="left" />
  </div>
);

const Subheading: React.FC<{ text: string }> = ({ text }) => (
  <div className="margin-top-10">
    <Text
      text={text}
      fontSize="16px"
      fontWeight="600"
      lineHeight="22px"
      color="#030303"
      textAlign="left"
    />
  </div>
);
